import { useState } from 'react';
import { X } from 'lucide-react';
import { ACTIVITY_TYPES } from '@app/common';
import type { ActivityLogEntry, ActivityMetadata, ActivityType } from '@app/common';
import { useAdminActivity, ADMIN_PAGE_SIZE } from '../../hooks/useAdminData';
import { useTranslation } from '../../context/LanguageContext';
import { ACTIVITY_TYPE_KEYS } from '../../i18n';
import { AdminPanel, AdminStateRow, Pager, formatDateTime } from './AdminShared';

/** Each event type gets its own accent, so the feed is scannable without reading. */
const TYPE_COLORS: Record<ActivityType, string> = {
  user_signed_up: 'bg-emerald-500/20 text-emerald-300',
  user_signed_in: 'bg-slate-600/40 text-slate-300',
  content_generated: 'bg-violet-500/20 text-violet-300',
  generation_blocked: 'bg-amber-500/20 text-amber-300',
  generation_failed: 'bg-rose-500/20 text-rose-300',
  brand_created: 'bg-sky-500/20 text-sky-300',
  account_connected: 'bg-emerald-500/20 text-emerald-300',
  account_disconnected: 'bg-slate-600/40 text-slate-300',
  account_reauth_required: 'bg-amber-500/20 text-amber-300',
  post_scheduled: 'bg-sky-500/20 text-sky-300',
  post_published: 'bg-emerald-500/20 text-emerald-300',
  post_failed: 'bg-rose-500/20 text-rose-300',
  post_approved: 'bg-emerald-500/20 text-emerald-300',
  post_rejected: 'bg-amber-500/20 text-amber-300',
  token_refresh_failed: 'bg-rose-500/20 text-rose-300',
  payment_initiated: 'bg-sky-500/20 text-sky-300',
  payment_completed: 'bg-emerald-500/20 text-emerald-300',
  payment_failed: 'bg-rose-500/20 text-rose-300',
  subscription_activated: 'bg-emerald-500/20 text-emerald-300',
  subscription_cancelled: 'bg-amber-500/20 text-amber-300',
  user_banned: 'bg-rose-500/20 text-rose-300',
  user_unbanned: 'bg-emerald-500/20 text-emerald-300',
  question_generated: 'bg-slate-600/40 text-slate-400',
};

/**
 * One line of plain detail per event, built from the JSONB payload.
 *
 * Values are read defensively: `metadata` is schemaless in Postgres, so old rows
 * written before a shape changed must still render rather than crash the feed.
 */
function describe(entry: ActivityLogEntry): string {
  const meta: ActivityMetadata = entry.metadata ?? {};
  const str = (key: string): string | null => {
    const value = meta[key];
    return typeof value === 'string' || typeof value === 'number' ? String(value) : null;
  };

  switch (entry.type) {
    case 'content_generated':
      return [str('brand'), str('pillar'), str('platforms')].filter(Boolean).join(' · ');
    case 'generation_blocked':
      return [str('reason'), str('plan')].filter(Boolean).join(' · ');
    case 'generation_failed': {
      const status = str('upstreamStatus');
      return [status ? `upstream ${status}` : null, str('brand')].filter(Boolean).join(' · ');
    }
    case 'brand_created':
      return [str('brand'), str('vertical')].filter(Boolean).join(' · ');
    case 'account_connected':
    case 'account_disconnected':
    case 'account_reauth_required':
    case 'token_refresh_failed':
      return [str('platform'), str('externalName'), str('reason')].filter(Boolean).join(' · ');
    case 'post_scheduled':
    case 'post_published':
    case 'post_approved':
    case 'post_rejected':
      return [str('platform'), str('brand'), str('scheduledAt')].filter(Boolean).join(' · ');
    case 'post_failed':
      return [str('platform'), str('errorCode'), str('attempt')].filter(Boolean).join(' · ');
    // Retired: rows written by the question generator. Kept so the feed renders
    // history rather than showing a blank detail line for older accounts.
    case 'question_generated': {
      const count = str('numberOfQuestions');
      return [str('subject'), str('examType'), count ? `${count} Q` : null]
        .filter(Boolean)
        .join(' · ');
    }
    case 'payment_initiated':
    case 'payment_completed':
    case 'payment_failed': {
      const amount = str('amount');
      return [str('plan'), amount ? `৳${amount}` : null, str('tranId')].filter(Boolean).join(' · ');
    }
    case 'subscription_activated':
      return str('plan') ?? '';
    case 'user_signed_up':
    case 'user_signed_in':
      return str('provider') ?? '';
    // The acting admin is the point of these two — `user_id` is the person acted
    // upon, so without the actor the row does not say who did it.
    case 'user_banned':
      return [str('actorEmail'), str('duration'), str('reason')].filter(Boolean).join(' · ');
    case 'user_unbanned':
      return str('actorEmail') ?? '';
    // `subscription_cancelled` carries no payload worth showing — the badge says
    // it all. Returning '' suppresses the detail line rather than printing a dash.
    default:
      return '';
  }
}

export function AdminActivityTab({
  userIdFilter,
  onClearUserFilter,
}: Readonly<{ userIdFilter?: string; onClearUserFilter: () => void }>) {
  const { t } = useTranslation();
  const [page, setPage] = useState(1);
  const [type, setType] = useState<ActivityType | ''>('');

  const { data, loading, error } = useAdminActivity(page, {
    userId: userIdFilter,
    type: type || undefined,
  });
  const entries = data?.items ?? [];
  const filtered = Boolean(userIdFilter) || type !== '';

  return (
    <AdminPanel
      title={t('nav.adminActivity')}
      actions={
        <>
          <select
            value={type}
            onChange={(e) => {
              setType(e.target.value as ActivityType | '');
              setPage(1);
            }}
            className="rounded-lg border border-slate-700 bg-slate-900 py-1.5 px-2 text-xs text-slate-200 focus:border-violet-500 focus:outline-none"
          >
            <option value="">{t('admin.filterAll')}</option>
            {ACTIVITY_TYPES.map((value) => (
              <option key={value} value={value}>
                {t(ACTIVITY_TYPE_KEYS[value])}
              </option>
            ))}
          </select>
          {filtered && (
            <button
              type="button"
              onClick={() => {
                setType('');
                onClearUserFilter();
                setPage(1);
              }}
              className="inline-flex items-center gap-1 rounded-lg border border-slate-700 px-2 py-1.5 text-xs text-slate-400 hover:bg-slate-700 transition-colors"
            >
              <X className="h-3 w-3" />
              {t('admin.filterClear')}
            </button>
          )}
        </>
      }
    >
      {userIdFilter && (
        <p className="px-5 py-2 bg-violet-500/10 border-b border-violet-500/20 text-xs text-violet-300">
          {t('admin.filteredToUser')}
        </p>
      )}

      <AdminStateRow
        loading={loading}
        error={error}
        empty={entries.length === 0}
        emptyLabel={t('admin.noActivity')}
      />

      {!loading && !error && entries.length > 0 && (
        <>
          <div className="divide-y divide-slate-700">
            {entries.map((entry) => {
              const detail = describe(entry);
              return (
                <div key={entry.id} className="flex items-start gap-3 px-5 py-3">
                  <span
                    className={`flex-shrink-0 rounded-full px-2 py-0.5 text-[10px] font-bold whitespace-nowrap ${TYPE_COLORS[entry.type]}`}
                  >
                    {t(ACTIVITY_TYPE_KEYS[entry.type])}
                  </span>
                  <div className="min-w-0 flex-1">
                    <p className="text-xs font-medium text-slate-200 truncate">
                      {entry.userEmail ?? entry.userId ?? t('common.emptyValue')}
                    </p>
                    {detail && <p className="text-[11px] text-slate-500 truncate">{detail}</p>}
                  </div>
                  <span className="flex-shrink-0 text-[11px] text-slate-500 whitespace-nowrap">
                    {formatDateTime(entry.createdAt)}
                  </span>
                </div>
              );
            })}
          </div>
          <Pager
            page={page}
            pageSize={ADMIN_PAGE_SIZE}
            total={data?.total ?? 0}
            onChange={setPage}
          />
        </>
      )}
    </AdminPanel>
  );
}
